import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { 
  GraduationCap, 
  Award, 
  Download, 
  Share2, 
  CheckCircle,
  ArrowLeft,
  Calendar,
  User,
  BookOpen
} from "lucide-react";

const Certificate = () => {
  const { certificateId } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const certificateRef = useRef(null);

  useEffect(() => {
    fetchCertificate();
  }, [certificateId]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/certificates/${certificateId}`, {
        withCredentials: true
      });
      setCertificate(response.data);
    } catch (error) {
      toast.error("Certificate not found");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Certificate - ${certificate.course_title}`,
          text: `I completed ${certificate.course_title} on LearnHub!`,
          url: shareUrl
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Certificate link copied to clipboard!");
    }
  };

  const handleDownload = () => {
    // Create a simple print-friendly version
    const printContent = `
      <html>
        <head>
          <title>Certificate - ${certificate.course_title}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Great+Vibes&display=swap');
            body { 
              font-family: 'Outfit', sans-serif; 
              margin: 0; 
              padding: 40px;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: #fafaf9;
            }
            .certificate {
              width: 800px;
              padding: 60px;
              background: white;
              border: 3px solid #6d28d9;
              border-radius: 16px;
              text-align: center;
              box-shadow: 0 10px 40px rgba(0,0,0,0.1);
            }
            .header { 
              color: #6d28d9; 
              font-size: 14px; 
              text-transform: uppercase; 
              letter-spacing: 3px; 
              margin-bottom: 20px;
            }
            .title { 
              font-family: 'Great Vibes', cursive;
              font-size: 48px; 
              color: #1c1917;
              margin: 20px 0;
            }
            .subtitle { 
              font-size: 16px; 
              color: #57534e; 
              margin-bottom: 30px;
            }
            .name { 
              font-size: 36px; 
              font-weight: 700; 
              color: #1c1917;
              margin: 30px 0;
              padding: 20px 0;
              border-top: 2px solid #e7e5e4;
              border-bottom: 2px solid #e7e5e4;
            }
            .course { 
              font-size: 20px; 
              color: #6d28d9;
              font-weight: 600;
              margin: 20px 0;
            }
            .instructor { 
              font-size: 14px; 
              color: #57534e;
              margin: 10px 0;
            }
            .date { 
              font-size: 14px; 
              color: #78716c;
              margin-top: 40px;
            }
            .cert-number {
              font-size: 12px;
              color: #a8a29e;
              margin-top: 20px;
              font-family: monospace;
            }
            .badge {
              width: 80px;
              height: 80px;
              margin: 0 auto 20px;
              background: linear-gradient(135deg, #6d28d9, #8b5cf6);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .badge svg {
              width: 40px;
              height: 40px;
              color: white;
            }
          </style>
        </head>
        <body>
          <div class="certificate">
            <div class="header">LearnHub Certificate of Completion</div>
            <div class="title">Certificate of Achievement</div>
            <div class="subtitle">This is to certify that</div>
            <div class="name">${certificate.user_name}</div>
            <div class="subtitle">has successfully completed the course</div>
            <div class="course">${certificate.course_title}</div>
            <div class="instructor">Instructor: ${certificate.course_instructor}</div>
            <div class="date">Completed on ${formatDate(certificate.completed_at)}</div>
            <div class="cert-number">Certificate Number: ${certificate.certificate_number}</div>
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Award className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Certificate Not Found</h2>
            <p className="text-stone-500 mb-4">This certificate may have been removed or doesn't exist.</p>
            <Link to="/dashboard">
              <Button className="bg-violet-700 hover:bg-violet-800">
                Go to Dashboard
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Link */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-stone-600 hover:text-violet-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>

        {/* Certificate Card */}
        <Card 
          ref={certificateRef}
          className="bg-white rounded-2xl border-2 border-violet-200 shadow-xl overflow-hidden"
          data-testid="certificate-card"
        >
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-violet-600 to-violet-800 px-8 py-6 text-center">
            <div className="inline-flex items-center gap-2 text-violet-200 text-sm uppercase tracking-widest mb-2">
              <GraduationCap className="h-5 w-5" />
              LearnHub Certificate of Completion
            </div>
          </div>

          {/* Certificate Content */}
          <CardContent className="p-8 md:p-12 text-center">
            {/* Badge */}
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-violet-600 to-violet-800 rounded-full flex items-center justify-center shadow-lg">
              <Award className="h-10 w-10 text-white" />
            </div>

            {/* Title */}
            <h1 
              className="text-4xl md:text-5xl font-bold text-stone-900 mb-4"
              style={{ fontFamily: 'Outfit' }}
            >
              Certificate of Achievement
            </h1>

            <p className="text-stone-500 mb-8">This is to certify that</p>

            {/* Recipient Name */}
            <div className="py-6 px-8 border-y-2 border-stone-100 mb-8">
              <h2 
                className="text-3xl md:text-4xl font-bold text-stone-900"
                style={{ fontFamily: 'Outfit' }}
                data-testid="certificate-recipient"
              >
                {certificate.user_name}
              </h2>
            </div>

            <p className="text-stone-500 mb-4">has successfully completed the course</p>

            {/* Course Title */}
            <h3 
              className="text-2xl md:text-3xl font-semibold text-violet-700 mb-4"
              style={{ fontFamily: 'Outfit' }}
              data-testid="certificate-course"
            >
              {certificate.course_title}
            </h3>

            {/* Course Details */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-stone-500 mb-8">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Instructor: {certificate.course_instructor}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>Completed: {formatDate(certificate.completed_at)}</span>
              </div>
            </div>

            {/* Certificate Number */}
            <div className="inline-flex items-center gap-2 bg-stone-100 rounded-full px-4 py-2 text-sm text-stone-600">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Certificate Number: <span className="font-mono font-medium">{certificate.certificate_number}</span>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-4">
          <Button 
            onClick={handleDownload}
            className="bg-violet-700 hover:bg-violet-800 rounded-full px-6"
            data-testid="download-certificate-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Certificate
          </Button>
          <Button 
            onClick={handleShare}
            variant="outline"
            className="rounded-full px-6 border-stone-200 hover:border-violet-700 hover:text-violet-700"
            data-testid="share-certificate-btn"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share Certificate
          </Button>
        </div>

        {/* Verification Notice */}
        <p className="text-center text-sm text-stone-400">
          This certificate can be verified at{" "}
          <span className="font-mono">
            {window.location.origin}/verify/{certificate.certificate_number}
          </span>
        </p>
      </div>
    </div>
  );
};

export default Certificate;
